
import { useEffect, useRef, useState } from 'react'
import { Body, Box, Dialog, Divider, Dot, ErrorText, Footer, Header, Inputs, Overlay, PinInput } from '../styles/ConfirmPinModalStyle'

interface Props {
   open: boolean
   onSuccess: () => void
}

export default function SetupPinModal({ open, onSuccess }: Props) {
   const [step, setStep] = useState<'new' | 'confirm'>('new')
   const [newPin, setNewPin] = useState(['', '', '', ''])
   const [confirmPin, setConfirmPin] = useState(['', '', '', ''])
   const [active, setActive] = useState(0)
   const [error, setError] = useState('')
   const [loading, setLoading] = useState(false)
   const r0 = useRef<HTMLInputElement>(null)
   const r1 = useRef<HTMLInputElement>(null)
   const r2 = useRef<HTMLInputElement>(null)
   const r3 = useRef<HTMLInputElement>(null)

   const vals = step === 'new' ? newPin : confirmPin
   const setVals = step === 'new' ? setNewPin : setConfirmPin

   useEffect(() => {
      if (open) {
         setStep('new')
         setNewPin(['', '', '', ''])
         setConfirmPin(['', '', '', ''])
         setActive(0)
         setError('')
         setTimeout(() => r0.current?.focus(), 10)
      }
   }, [open])

   if (!open) return null

   async function submit(v: string[]) {
      if (v.every(d => d !== '')) {
         if (step === 'new') {
            setStep('confirm')
            setActive(0)
            setError('')
            setTimeout(() => r0.current?.focus(), 10)
         } else {
            const pin = newPin.join('')
            const confirm = v.join('')

            if (pin !== confirm) {
               setError('PINs do not match')
               setConfirmPin(['', '', '', ''])
               setActive(0)
               r0.current?.focus()
               return
            }

            setLoading(true)
            try {
               const { authService } = await import('../services/AuthService')
               const response = await authService.setupPin({
                  pin,
                  confirmPin: confirm
               })

               if (response.status === 'success') {
                  onSuccess()
               } else {
                  setError(response.message || 'Failed to setup PIN')
               }
            } catch (err) {
               setError(err instanceof Error ? err.message : 'Failed to setup PIN')
            } finally {
               setLoading(false)
            }
         }
      }
   }

   function handleInput(i: number, ch: string) {
      setError('')
      const d = ch.replace(/\D/g, '').slice(-1)
      if (!d) return

      const v = [...vals]
      v[i] = d
      setVals(v)

      if (i < 3) {
         setActive(i + 1)
            ;[r0, r1, r2, r3][i + 1]?.current?.focus()
      }
   }

   function handleKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === 'Backspace') {
         const v = [...vals]
         if (v[i]) {
            v[i] = ''
            setVals(v)
         } else if (i > 0) {
            setActive(i - 1)
               ;[r0, r1, r2, r3][i - 1]?.current?.focus()
            const vv = [...vals]
            vv[i - 1] = ''
            setVals(vv)
         }
         e.preventDefault()
      }
   }

   return (
      <Overlay>
         <Dialog onClick={e => e.stopPropagation()} role='dialog' aria-modal='true'>
            <Header>
               <h2>{step === 'new' ? 'Create PIN' : 'Confirm PIN'}</h2>
            </Header>
            <Body>
               <span className='wrap-break-word'>
                  {step === 'new'
                     ? 'Create a 4-digit PIN to secure your account'
                     : 'Re-enter your PIN to confirm'}
               </span>
               <Inputs>
                  {vals.map((val, i) => (
                     <Box key={i} $active={active === i} $error={!!error} onClick={() => { setActive(i);[r0, r1, r2, r3][i]?.current?.focus() }}>
                        <PinInput
                           ref={[r0, r1, r2, r3][i]}
                           value={val}
                           onChange={e => handleInput(i, e.target.value)}
                           onKeyDown={e => handleKey(i, e)}
                           inputMode='numeric'
                           maxLength={1}
                           aria-label={`Digit ${i + 1}`}
                        />
                        {val ? <Dot /> : <Dot style={{ visibility: 'hidden' }} />}
                     </Box>
                  ))}
               </Inputs>
               {error && <ErrorText>{error}</ErrorText>}
            </Body>
            <Divider />
            <Footer>
               <button
                  className={`confirm ${vals.every(v => v) ? 'enabled' : ''}`}
                  onClick={() => submit(vals)}
                  disabled={loading || !vals.every(v => v)}
               >
                  {loading ? 'Setting up...' : step === 'new' ? 'Continue' : 'Confirm'}
               </button>
            </Footer>
         </Dialog>
      </Overlay>
   )
}
